<template>
  <admin-input-list-wrapper>
    <template #title>
      <span class="font-weight-medium">
        {{ $i18n.t('admin.usersList') }}
      </span>
    </template>
    <template #button>
      <base-button
        @click="openAddUserModal"
        translation-path="admin.addUser"
        style="margin-right: 0px !important"
      ></base-button>
    </template>
    <template #list>
      <v-list dense>
        <template v-for="(value, key) in searchedItems">
          <users-input-list-item-wrapper
            :key="key"
            v-bind="value"
            @dotsItemClicked="onDotsItemClicked"
          ></users-input-list-item-wrapper>
        </template>
      </v-list>
    </template>
    <template #dialogs>
      <main-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :title="$i18n.t(dialogTitle)"
        :body="$i18n.t(dialogBody, { name: payload.name })"
        :agree-text="dialogAgreeButton"
        @agree="dialogAction"
        :loading="isConfirmButtonLoading"
      >
      </main-dialog>
      <main-input-dialog
        ref="userAdding"
        dialog-width="40%"
        v-if="isInputDialogLoaded"
        :is-visible.sync="isInputDialogVisible"
        :title-translation="inputDialogTitle"
        :agree-text="inputDialogAgree"
        @agree="inputDialogAction"
        :inputs="addUserInputs"
        :loading="isAgreeButtonLoading"
      >
        <template #name.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="payload.name" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #email.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="payload.email" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #group_id.input="{ hint, dataType }">
          <v-col cols="6">
            <v-row no-gutters>
              <v-col cols="12">
                <data-input
                  :menu-props="{ offsetY: true, maxWidth: '380px' }"
                  clearable
                  v-model="currentGroup"
                  :items="groupsArray"
                  item-text="name"
                  :return-object="true"
                  :disabled="isCurrentUserEdited"
                  :hint="$i18n.t(hint)"
                  persistent-hint
                  :data-type="dataType"
                >
                </data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #password.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="payload.password" v-bind="slot" :rules="passwordRules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #repeatPassword.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="repeatedPassword" v-bind="slot" :rules="repeatedPasswordRules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </main-input-dialog>
    </template>
    <template #search>
      <data-input
        :data-type="{ name: 'text' }"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$i18n.t('default.find')"
        v-model="search"
        clearable
        height="32"
        hide-details
        class="searchInput"
      >
      </data-input>
    </template>
  </admin-input-list-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';

import UsersInputListItemWrapper from '@/components/UsersInputListItemWrapper';
import DataInput from '@/components/DataInput';
import AdminInputListWrapper from '@/components/AdminInputListWrapper';
import passwordRules from '@/mixins/passwordRules';

export default {
  name: 'UsersInput',
  mixins: [passwordRules],
  components: {
    DataInput,
    UsersInputListItemWrapper,
    AdminInputListWrapper,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  data: () => ({
    search: '',
    id: undefined,
    dialogType: undefined,
    isDialogLoaded: false,
    isDialogVisible: false,
    isInputDialogLoaded: false,
    isInputDialogVisible: false,
    repeatedPassword: '',
    isEdit: false,
    isAgreeButtonLoading: false,
    isConfirmButtonLoading: false,
    payload: {
      email: undefined,
      password: undefined,
      group_id: undefined,
      name: undefined,
    },
    inputDialogTitle: '',
  }),
  computed: {
    users: get('users/users'),
    groups: get('users/groups'),
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return Object.values(this.sortItems()).filter(user =>
        user.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    groupsArray() {
      return this.$_objectToArray(this.groups);
    },
    currentGroup: {
      get() {
        return this.groups[this.payload.group_id];
      },
      set(nV) {
        this.payload.group_id = nV ? nV.id : undefined;
      },
    },
    currentUser() {
      return this.users[this.id];
    },
    dialogTitle() {
      return `dialog.title.${this.dialogType}User`;
    },
    dialogBody() {
      return `dialog.body.${this.dialogType}User`;
    },
    dialogAgreeButton() {
      return `menu.${this.dialogType}`;
    },
    dialogAction() {
      return this[`${this.dialogType}User`];
    },
    inputDialogAction() {
      return this.isEdit ? this.editUser : this.addUser;
    },
    inputDialogAgree() {
      return this.isEdit ? 'button.save' : 'dialog.agreeAdd';
    },
    samePasswordRule() {
      return this.repeatedPassword === this.payload.password || 'rules.samePassword';
    },
    repeatedPasswordRules() {
      if (this.isEdit) {
        return this.payload.password ? [v => !!v || 'rules.required', this.samePasswordRule] : [];
      }
      return [v => !!v || 'rules.required', this.samePasswordRule];
    },
    passwordRules() {
      if (this.isEdit) {
        return this.payload.password ? [v => !!v || 'rules.required', ...this.computedRules] : [];
      }
      return [v => !!v || 'rules.required', ...this.computedRules];
    },
    usernameMaxLength: get('admin/settingsValues@user_name_max_length'),
    addUserInputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          hint: 'dialog.body.createUsername',
          'validate-on-blur': !this.isEdit,
          rules: [
            v => !!v || 'rules.required',
            v => (v ? v : '').length >= 2 || 'rules.usernameLength',
            v => /^[a-zA-Z0-9\s\\.\\_]+$/.test(v) || 'rules.allowedSpecialChars',
            v => !this.$_validateEmail(v) || 'rules.usernameNotEmail',
            v => this.$_validateUsername(v, 2) || 'rules.usernameFirstLetters',
          ],
          dataType: {
            max_length: this.usernameMaxLength,
            name: 'text',
          },
        },
        {
          name: 'email',
          subtitle: 'dialog.body.optional',
          'validate-on-blur': !this.isEdit,
          translationPath: 'dialog.body.email',
          rules: [v => !v || this.$_validateEmail(v) || 'rules.email'],
          hint: 'dialog.body.typeEmail',
          dataType: {
            name: 'text',
          },
          autocomplete: 'new-password',
        },
        {
          name: 'group_id',
          subtitle: 'dialog.body.optional',
          'validate-on-blur': !this.isEdit,
          translationPath: 'dialog.body.group',
          hint: 'dialog.body.choseGroup',
          dataType: {
            name: 'select',
          },
        },
        {
          'validate-on-blur': !this.isEdit,
          name: 'password',
          translationPath: this.isEdit ? 'dialog.body.newPassword' : 'dialog.body.password',
          hint: this.isEdit ? 'dialog.body.createNewPassword' : 'dialog.body.createPassword',

          type: 'password',
          dataType: {
            name: 'text',
          },
          autocomplete: 'new-password',
        },
        {
          name: 'repeatPassword',
          'validate-on-blur': !this.isEdit,
          translationPath: 'dialog.body.repeatPassword',
          hint: 'dialog.body.repeatPassword',
          type: 'password',
          dataType: {
            name: 'text',
          },
          autocomplete: 'new-password',
        },
      ];
    },
    currentLoggedUserId: get('users/currentUser@id'),
    isCurrentUserEdited() {
      return this.currentLoggedUserId === this.id;
    },
  },
  watch: {
    isInputDialogVisible(nV) {
      if (!nV) {
        this.isEdit = false;
        this.resetPayload();
      }
    },
    isDialogVisible(nV) {
      if (!nV) {
        this.resetPayload();
      }
    },
  },
  methods: {
    editUserAction: call('users/editUser'),
    deleteUserAction: call('users/deleteUser'),
    addUserAction: call('users/addUser'),
    logoutUserAction: call('users/logoutUser'),
    resetPayload() {
      this.payload.name = undefined;
      this.payload.group_id = undefined;
      this.payload.email = undefined;
      this.id = undefined;
    },
    onDotsItemClicked(data) {
      const { id, type } = data;
      this.id = id;
      const { name, email, group_id } = this.currentUser;
      this.payload = { email, name, group_id };
      if (type === 'edit') {
        this.isEdit = true;
        this.payloadCopy = { ...this.payload };
        this.openAddUserModal();
        this.inputDialogTitle = this.$i18n.t('dialog.title.editUser');
      } else {
        this.dialogType = type;
        this.isDialogLoaded = true;
        this.isDialogVisible = true;
      }
    },
    blockUser() {
      this.handleBlockUser(true);
    },
    unblockUser() {
      this.handleBlockUser(false);
    },
    async handleBlockUser(isBlock) {
      try {
        this.isConfirmButtonLoading = true;
        await this.editUserAction({
          id: this.id,
          body: { blocked: isBlock },
        });
      } catch (error) {
        console.log('handleBlockUser -> error', error);
      }
      this.closeConfirmationDialog();
    },
    async logOutUser() {
      try {
        this.isConfirmButtonLoading = true;
        await this.logoutUserAction({
          id: this.id,
          tokenId: this.currentUser['token_id'],
        });
      } catch (error) {
        console.log('error:', error);
      }
      this.closeConfirmationDialog();
    },
    closeConfirmationDialog() {
      this.isConfirmButtonLoading = false;
      this.isDialogVisible = false;
    },
    async deleteUser() {
      try {
        this.isConfirmButtonLoading = true;
        await this.deleteUserAction(this.id);
      } catch (error) {
        console.log('handleBlockUser -> error', error);
      }
      this.closeConfirmationDialog();
    },
    openAddUserModal() {
      this.isInputDialogVisible = true;
      this.isInputDialogLoaded = true;
      this.inputDialogTitle = this.$i18n.t('dialog.title.addUser');
    },
    addUser() {
      if (this.payload.email === '') {
        this.payload.email = undefined;
      }
      this.editAddWrapper('addUserAction', { ...this.payload });
    },
    editUser() {
      const changedKeys = Object.keys(this.payload).filter(key => {
        if (key === 'password') {
          return this.payload[key];
        }
        return this.payload[key] !== this.payloadCopy[key];
      });
      if (changedKeys.length === 0) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('admin.noChanges'),
          color: '#333333',
          buttonType: 'text',
        });
        return;
      }
      const body = changedKeys.reduce((total, current) => {
        return { ...total, [current]: this.payload[current] || null };
      }, {});
      this.editAddWrapper('editUserAction', { id: this.id, body });
    },
    closeDialog() {
      setTimeout(() => {
        this.$refs?.userAdding.$refs?.form.reset();
        this.isAgreeButtonLoading = false;
        this.isInputDialogVisible = false;
      }, 500);
    },
    async editAddWrapper(action, payload) {
      try {
        this.isAgreeButtonLoading = true;
        await this[action](payload);
      } catch (error) {
        console.log('editAddWrapper -> error', error);
      }
      this.closeDialog();
    },
    sortItems() {
      return this.$_objectToArray(this.users)?.sort((a, b) => b.id - a.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-input {
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: center;
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
